import React, { useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { LanguageProvider } from './i18n/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import Library from './pages/Library';
import Subscription from './pages/Subscription';
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './pages/Login';
import Register from './pages/Register';
import AdminDashboard from './pages/AdminDashboard';
import Profile from './pages/Profile';
import PostDetail from './pages/PostDetail';
import Articles from './pages/Articles';
import Videos from './pages/Videos';
import Simulations from './pages/Simulations';
import SimulationDetail from './pages/SimulationDetail';
import VideoDetail from './pages/VideoDetail';
import AuthCallback from './pages/AuthCallback';
import CourseEditor from './pages/CourseEditor';
import CourseLearn from './pages/CourseLearn';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from './components/ui/sonner';
import { ChevronUp } from 'lucide-react';
import { SeoManager } from './lib/seo';
import PaymentResult from './pages/PaymentResult';
import GoogleCallback from './pages/GoogleCallback';

const FloatingButtons = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!show) return null;
  return (
    <button data-testid="scroll-top-btn" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full btn-primary-blue text-white flex items-center justify-center shadow-lg transition-colors">
      <ChevronUp className="w-6 h-6" />
    </button>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

const AppRouter = () => {
  const location = useLocation();
  useEffect(() => {
    document.documentElement.classList.toggle('site-public', !location.pathname.startsWith('/admin'));
  }, [location.pathname]);
  // Synchronously check for OAuth session_id in URL fragment to prevent races
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <SeoManager />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/courses/:id" element={<CourseDetail />} />
        <Route path="/courses/:id/learn" element={<CourseLearn />} />
        <Route path="/library" element={<Library />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/videos" element={<Videos />} />
        <Route path="/simulations" element={<Simulations />} />
        <Route path="/simulations/:id" element={<SimulationDetail />} />
        <Route path="/quizzes/*" element={<Navigate to="/simulations" replace />} />
        <Route path="/subscription" element={<Subscription />} />
        <Route path="/payment/success" element={<PaymentResult />} />
        <Route path="/payment/cancel" element={<PaymentResult />} />
        <Route path="/auth/google" element={<GoogleCallback />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/courses/new" element={<CourseEditor />} />
        <Route path="/admin/courses/:id" element={<CourseEditor />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/library/post/:id" element={<PostDetail />} />
        <Route path="/library/video/:id" element={<VideoDetail />} />
      </Routes>
      {!/\/learn$/.test(location.pathname) && <><Footer /><FloatingButtons /></>}
    </>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <div className="App min-h-screen bg-slate-50 text-slate-900">
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster />
          <SonnerToaster position="top-center" richColors />
        </div>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
